import { useEffect, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { useNavigate, useLocation } from 'react-router-dom'
import { cloneDeep } from 'lodash-es'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText
} from '@mui/material'

import {
  useDeleteQRAAssessmentMutation,
  useGetQRAAssessmentQuery,
  useUpdateQRAAssessmentMutation
} from 'app/services/api/qra/assessments'
import { useGetQRASavedStateQuery } from 'app/services/api/qra/savedstates'
import { qraSystemGraphsViewRedirectHelper } from 'common/utils/navigation/qraNavUtils'
import { IStateAssessment } from 'features/RiskManagement/Assessments'
import { WISAssessmentStatusCode } from 'features/RiskManagement/Assessments/WISAssessmentStatusCode'
import { WISCreateModalQueryString } from 'features/RiskManagement/Assessments/Scenarios/WISCreateModalQueryString'

interface ISavedStateAssessmentOperators {
  assessmentId: string | undefined
}

/**
 * The [DISCARD | RERUN | PUBLISH] buttons
 */
export const SavedStateAssessmentOperators = ({
  assessmentId
}: ISavedStateAssessmentOperators) => {
  const { currentData: assessment } = useGetQRAAssessmentQuery(
    assessmentId ?? skipToken
  )

  if (assessment == null || assessment.isPublished) {
    return null
  }

  return (
    <>
      <div className='mx-4 h-8 border-l' />
      <div>
        <DiscardButtonAndDialog assessment={assessment} />
        <RerunButton />
        <PublishButtonAndDialog assessment={assessment} />
      </div>
    </>
  )
}

interface IButtonAndDialog {
  assessment: IStateAssessment
}

const DiscardButtonAndDialog = ({ assessment }: IButtonAndDialog) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [showDiscardDialog, setShowDiscardDialog] = useState<boolean>(false)

  const [deleteAssessment, { isLoading: isDiscarding }] =
    useDeleteQRAAssessmentMutation()

  const discardAssessment = (): void => {
    deleteAssessment(assessment)
      .unwrap()
      .then(() => {
        qraSystemGraphsViewRedirectHelper(location, navigate, false)
      })
      .finally(() => {
        setShowDiscardDialog(false)
      })
  }

  const handleClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick') {
      return
    }

    setShowDiscardDialog(false)
  }

  return (
    <>
      <button
        className='p-1 uppercase text-gray-700'
        onClick={() => setShowDiscardDialog(true)}
      >
        Discard
      </button>
      <Dialog
        open={showDiscardDialog}
        onClose={handleClose}
        maxWidth='xs'
        fullWidth
      >
        <DialogContent>
          <DialogContentText>
            {isDiscarding
              ? 'Discarding Scenario.. please wait'
              : 'Discard Scenario?'}
          </DialogContentText>
        </DialogContent>
        {!isDiscarding && (
          <DialogActions className=''>
            <button
              className={[
                'px-4 py-2',
                'rounded',
                'text-gray-700',
                'font-semibold uppercase'
              ].join(' ')}
              onClick={() => setShowDiscardDialog(false)}
            >
              Cancel
            </button>
            <button
              className={[
                'px-4 py-2',
                'rounded',
                'text-white',
                'bg-lighthouseBlue',
                'font-semibold uppercase'
              ].join(' ')}
              onClick={discardAssessment}
            >
              Discard
            </button>
          </DialogActions>
        )}
      </Dialog>
    </>
  )
}

const RerunButton = () => {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()

  const openWISRerunModal = () => {
    const searchParams = new URLSearchParams(search)

    searchParams.set(WISCreateModalQueryString.IsOpen, 'true')
    searchParams.set(WISCreateModalQueryString.IsRerun, 'true')
    navigate(`${pathname}?${searchParams.toString()}`)
  }

  return (
    <button
      className='mx-3 p-1 uppercase text-lighthouseBlue'
      onClick={openWISRerunModal}
    >
      Rerun
    </button>
  )
}

const PublishButtonAndDialog = ({ assessment }: IButtonAndDialog) => {
  const [showPublishDialog, setShowPublishDialog] = useState<boolean>(false)
  const [isErroredAssessment, setIsErroredAssessment] = useState<boolean>(false)

  const [updateAssessment, { isLoading: isPublishing }] =
    useUpdateQRAAssessmentMutation()

  const savedStates = useGetQRASavedStateQuery(
    assessment?.savedStateId.toString()
  )

  useEffect(() => {
    setIsErroredAssessment(false)

    savedStates?.currentData?.assessments.some((a) => {
      if (
        a.id === assessment.id &&
        a.statusCode === WISAssessmentStatusCode.Error
      ) {
        setIsErroredAssessment(true)

        return true
      }

      return false
    })
  }, [savedStates, assessment])

  const publishAssessment = (): void => {
    const assessmentToUpdate = cloneDeep(assessment)

    /**
     * REVIEW: May also want to unset \/^assessment*$\/ attributes as saved
     *         states API currently returns dummy empty array value which is
     *         not representive of content when retrieved from assessments API
     */
    assessmentToUpdate.isPublished = true

    updateAssessment(assessmentToUpdate).finally(() => {
      setShowPublishDialog(false)
    })
  }

  const handleClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick') {
      return
    }

    setShowPublishDialog(false)
  }

  return (
    <>
      <button
        className={[
          'px-3 py-2',
          'rounded-sm',
          'bg-lighthouseBlue text-white',
          'uppercase',
          isErroredAssessment
            ? 'cursor-not-allowed opacity-50'
            : 'hover:opacity-80'
        ].join(' ')}
        onClick={() => setShowPublishDialog(true)}
        disabled={isErroredAssessment}
      >
        Publish
      </button>
      <Dialog
        open={showPublishDialog}
        onClose={handleClose}
        maxWidth='xs'
        fullWidth
      >
        <DialogContent>
          <DialogContentText>
            {isPublishing ? 'Publishing Scenario' : 'Publish Scenario?'}
          </DialogContentText>
        </DialogContent>
        {!isPublishing && (
          <DialogActions className=''>
            <button
              className={[
                'px-4 py-2',
                'rounded',
                'text-gray-700',
                'font-semibold uppercase'
              ].join(' ')}
              onClick={() => setShowPublishDialog(false)}
            >
              Cancel
            </button>
            <button
              className={[
                'px-4 py-2',
                'rounded',
                'text-white',
                'bg-lighthouseBlue',
                'font-semibold uppercase'
              ].join(' ')}
              onClick={publishAssessment}
            >
              Publish
            </button>
          </DialogActions>
        )}
      </Dialog>
    </>
  )
}
